import React, { useContext, useEffect, useReducer } from "react";
import { 
    Box, Button, Typography, TextField, MenuItem, 
    FormControl, Select, InputAdornment, Dialog, DialogContent, InputLabel, CircularProgress
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTicketAlt, faTag, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { createPromotion } from "../../../controllers/company/loyalty/PromotionsController";
import UserContext from "../../../navigation/context/userContext";

// =====================================================================
// 1. ESTADO INICIAL Y REDUCER (Optimizado)
// =====================================================================
const initialState = {
    title: '',                  errorTitle: false,
    modality: 'free_claim',
    discount_type: 'percentage',
    discount_value: '',         errorDiscountValue: false,
    global_stock: '',
    usage_limit_per_user: '',
    valid_from: '',             
    valid_until: '',            errorDates: false,
    
    isLoading: false,
};

function reducer(state, action) {
    if (action.type === 'SET_MULTIPLE') {
        return { ...state, ...action.payload };
    }
    return {
        ...state,
        [action.field]: action.value
    };
}

// =====================================================================
// 2. COMPONENTE PRINCIPAL
// =====================================================================
export default function CreatePromotionModal({ open, onClose }) {
    const { userData } = useContext(UserContext);
    const [state, dispatch] = useReducer(reducer, initialState);

    // Desestructuramos para tener el código limpio en el JSX
    const {
        title, errorTitle,
        modality, discount_type, discount_value, errorDiscountValue,
        global_stock, usage_limit_per_user,
        valid_from, valid_until, errorDates,
        isLoading
    } = state;

    useEffect(()=>{
        console.log(userData);
    },[]);

    const handleChange = (e) => {
        dispatch({ field: e.target.name, value: e.target.value });
    };

    // =====================================================================
    // 3. VALIDACIÓN CENTRALIZADA
    // =====================================================================
    const formVerification = () => {
        let ban = 0;
        
        // Objeto temporal para actualizar todos los errores de una vez
        let nextErrors = {
            errorTitle: false,
            errorDiscountValue: false,
            errorDates: false
        };

        if (title.trim() === '') {
            nextErrors.errorTitle = true;
            ban = 1;
        }

        if (discount_type !== 'bogo' && (discount_value === '' || parseFloat(discount_value) <= 0)) {
            nextErrors.errorDiscountValue = true;
            ban = 1;
        }

        // Validación lógica de fechas (si ambas existen, Hasta debe ser mayor que Desde)
        if (valid_from !== '' && valid_until !== '') {
            if (new Date(valid_until) < new Date(valid_from)) {
                nextErrors.errorDates = true;
                ban = 1;
            }
        }

        // Disparamos la actualización al estado global de React
        dispatch({ type: 'SET_MULTIPLE', payload: nextErrors });
        return ban;
    };

    const handleSave = async () => {
        // Validamos primero
        if (formVerification() === 1) {
            return; // Si hay errores, detenemos el proceso
        }

        // Iniciamos la carga
        dispatch({ field: 'isLoading', value: true });
        const merchant_id = userData.entity.id;

        const response = await createPromotion(
            merchant_id,
            title, modality, discount_type, discount_value, 
            global_stock, usage_limit_per_user, valid_from, valid_until
        );
        console.log(response);

        //console.log("Enviando al Backend (Laravel):", payload);

        dispatch({ field: 'isLoading', value: false });
    };

    return (
        <Dialog 
            open={open} 
            onClose={!isLoading ? onClose : undefined} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    boxShadow: '0px 20px 40px rgba(0,0,0,0.08)'
                }
            }}
        >
            {/* HEADER VISUAL (Oscuro) */}
            <Box sx={{ bgcolor: 'primary.main', p: 2, textAlign: 'center', borderRadius: 3 }}>
                <Box sx={{ 
                    width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.1)', 
                    borderRadius: '50%', display: 'flex', alignItems: 'center', 
                    justifyContent: 'center', margin: '0 auto', mb: 2
                }}>
                    <FontAwesomeIcon icon={faTicketAlt} size="lg" color="#fff" />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: '800', color: '#fff', mb: 0.5, letterSpacing: -0.5 }}>
                    Nueva Promoción
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Diseña una oferta irresistible para atraer más clientes.
                </Typography>
            </Box>

            {/* CONTENIDO SCROLLABLE (Sin barra de scroll visible) */}
            <DialogContent 
                sx={{ 
                    pt:3,
                    pl:0,
                    pr:0,
                    msOverflowStyle: 'none', 
                    scrollbarWidth: 'none', 
                    '&::-webkit-scrollbar': { display: 'none' } 
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    
                    {/* SECCIÓN 1: DETALLES DEL BENEFICIO (Isla Gris) */}
                    <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 3, border: '1px solid #f1f5f9' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <FontAwesomeIcon icon={faTag} color="#64748b" style={{ marginRight: 8 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Detalles del Beneficio
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Título */}
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Título de la Promoción"
                                    name="title"
                                    value={title}
                                    onChange={handleChange}
                                    placeholder="Ej: 15% Off en Hamburguesas"
                                    error={errorTitle}
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                {errorTitle ? (
                                    <Typography variant="body2" color="error" sx={{ mt: 0.5, ml: 1, fontWeight: 500 }}>
                                        El título es obligatorio.
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" sx={{ color: '#888', mt: 0.5, ml: 1 }}>
                                        El título principal que verán tus clientes en la aplicación.
                                    </Typography>
                                )}
                            </Box>

                            {/* Contenedor Flex: En móvil se apila, en PC lado a lado */}
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                                
                                {/* Modalidad */}
                                <Box sx={{ flex: 1 }}>
                                    <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 2 }}>
                                        <InputLabel>Público Objetivo</InputLabel>
                                        <Select
                                            name="modality"
                                            value={modality}
                                            onChange={handleChange}
                                            label="Público Objetivo"
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="free_claim">Público General</MenuItem>
                                            <MenuItem value="qud_pass_exclusive">Exclusivo QUD Pass</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography variant="body2" sx={{ color: '#888', mt: 0.5, ml: 1 }}>
                                        ¿Quiénes pueden ver y reclamar esto?
                                    </Typography>
                                </Box>

                                {/* Tipo de Descuento */}
                                <Box sx={{ flex: 1 }}>
                                    <FormControl fullWidth sx={{ bgcolor: '#fff', borderRadius: 2 }}>
                                        <InputLabel>Tipo de Oferta</InputLabel>
                                        <Select
                                            name="discount_type"
                                            value={discount_type}
                                            onChange={handleChange}
                                            label="Tipo de Oferta"
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <MenuItem value="percentage">Porcentaje (%)</MenuItem>
                                            <MenuItem value="fixed">Monto Fijo ($)</MenuItem>
                                            <MenuItem value="bogo">2x1 (BOGO)</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography variant="body2" sx={{ color: '#888', mt: 0.5, ml: 1 }}>
                                        El formato de tu descuento.
                                    </Typography>
                                </Box>

                                {/* Valor (Dinámico) */}
                                {discount_type !== 'bogo' && (
                                    <Box sx={{ flex: 1 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Valor"
                                            name="discount_value"
                                            value={discount_value}
                                            onChange={handleChange}
                                            error={errorDiscountValue}
                                            sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">{discount_type === 'percentage' ? '%' : '$'}</InputAdornment>,
                                            }}
                                        />
                                        {errorDiscountValue && (
                                            <Typography variant="body2" color="error" sx={{ mt: 0.5, ml: 1, fontWeight: 500 }}>
                                                Ingresa un valor válido.
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* SECCIÓN 2: REGLAS Y LÍMITES (Isla Gris) */}
                    <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 3, border: '1px solid #f1f5f9' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <FontAwesomeIcon icon={faShieldAlt} color="#64748b" style={{ marginRight: 8 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Reglas y Límites
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            
                            {/* Stock y Uso */}
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Stock Total"
                                        name="global_stock"
                                        value={global_stock}
                                        onChange={handleChange}
                                        placeholder="Ilimitado"
                                        sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                    <Typography variant="body2" sx={{ color: '#888', mt: 0.5, ml: 1 }}>
                                        Límite general de esta campaña.
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Límite por persona"
                                        name="usage_limit_per_user"
                                        value={usage_limit_per_user}
                                        onChange={handleChange}
                                        placeholder="Ilimitado"
                                        sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                    <Typography variant="body2" sx={{ color: '#888', mt: 0.5, ml: 1 }}>
                                        Veces que un cliente puede usarlo.
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Fechas */}
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Válido Desde"
                                    name="valid_from"
                                    value={valid_from}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    error={errorDates}
                                    sx={{ flex: 1, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Válido Hasta"
                                    name="valid_until"
                                    value={valid_until}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    error={errorDates}
                                    sx={{ flex: 1, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Box>
                            
                            {errorDates && (
                                <Typography variant="body2" color="error" sx={{ ml: 1, fontWeight: 500, mt: -1 }}>
                                    La fecha de fin debe ser posterior a la fecha de inicio.
                                </Typography>
                            )}

                        </Box>
                    </Box>

                </Box>
            </DialogContent>

            {/* BOTONERA (Sin type="submit") */}
            <Box sx={{ display: 'flex', gap: 2, p: 3, pt: 0, bgcolor: '#fff', justifyContent: 'flex-end' }}>
                <Button 
                    variant="text" 
                    onClick={onClose}
                    disabled={isLoading}
                    sx={{ color: '#64748b', fontWeight: 'bold', textTransform: 'none', px: 3 }}
                >
                    Cancelar
                </Button>
                <Button 
                    onClick={handleSave}
                    variant="contained" 
                    disableElevation
                    disabled={isLoading}
                    sx={{ 
                        bgcolor: '#0f172a', 
                        py: 1.2, px: 4, 
                        minWidth: 180,
                        borderRadius: 2,
                        fontWeight: 'bold', 
                        textTransform: 'none', 
                        '&:hover': { bgcolor: '#1e293b' } 
                    }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Publicar Promoción'}
                </Button>
            </Box>
        </Dialog>
    );
}